import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Chip,
  Alert,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import useGroupsApi from "../../../../hooks/useGroupsApi";
import useNotification from "../../../../hooks/useNotification";
import NotificationSnackbar from "../../../../components/NotificationSnackbar.jsx";
import { useRoleNavigator } from "../../../../utils/roleNavigator.js";
import { handleFieldChange } from "../../../../utils/handleFieldChange.js";
import { sanitizeInput } from "../../../../utils/inputSanitizer.js";

/* eslint-disable */

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 300,
    },
  },
};

export default function GroupDetailsPage() {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const { id } = useParams();
  const notificationApi = useNotification();
  const { notification, hideNotification, showNotification } = notificationApi;
  const {
    loading,
    group,
    operators,
    clients,
    fetchGroupById,
    fetchOperators,
    fetchClients,
    updateGroup,
  } = useGroupsApi(showNotification);

  // State

  const [validationErrors, setValidationErrors] = useState({});
  const [searchText, setSearchText] = useState("");
  const [filterOperators, setFilterOperators] = useState([]);

  const handleOperatorSearch = useCallback(
    (e) => {
      const text = e.target.value;
      setSearchText(text);

      if (!text.trim()) {
        setFilterOperators([]);
        return;
      }

      // Filter operators by name or email
      const filtered = operators.filter(
        (op) =>
          (op.name && op.name.toLowerCase().includes(text.toLowerCase())) ||
          (op.email && op.email.toLowerCase().includes(text.toLowerCase()))
      );
      setFilterOperators(filtered);
    },
    [operators]
  );

  const [formData, setFormData] = useState({
    name: "",
    operatorIds: [],
    clientIds: [],
  });

  const navigateByRole = useRoleNavigator();
  // Load Functions
  const loadGroupDetails = async () => {
    const result = await fetchGroupById(id);
    if (!result.success) {
      //navigate("/superadmin/groups");
      navigateByRole("groups");
    }
  };

  useEffect(() => {
    if (id) {
      loadGroupDetails();
      fetchOperators();
      fetchClients();
    }
  }, [id]);

  // Update form data when group is loaded
  useEffect(() => {
    if (group) {
      setFormData({
        name: group.name,
        operatorIds: group.assignedOperatorIds || [],
        clientIds: group.assignedClientIds || [],
      });
    }
  }, [group]);

  // Event Handlers
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: null }));
    }
    setSearchText("");
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    await updateGroup(id, {
      name: formData.name,
      operatorIds: formData.operatorIds,
      clientIds: formData.clientIds,
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = "Group name is required";
    }

    if (formData.operatorIds.length === 0) {
      errors.operatorIds = "At least one operator must be selected";
    }

    if (formData.clientIds.length === 0) {
      errors.clientIds = "At least one client must be selected";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getSelectedNames = (selectedIds, items) => {
    return selectedIds.map((id) => {
      const item = items.find((item) => item.id === id);
      return item
        ? item.firstName
          ? `${item.firstName} ${item.lastName}`
          : item.name
        : "Unknown";
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading group details...</Typography>
      </Box>
    );
  }

  if (!group) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Group not found</Typography>
      </Box>
    );
  }

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ color: "var(--color-primary)", fontWeight: 700 }}
          >
            EDIT GROUPS
          </Typography>
        </div>
        <Button
          variant="outlined"
          // onClick={() => navigate("/groups")}
          onClick={() => navigateByRole("groups")}
          sx={{
            color: "#fff",
            borderColor: "#635f5fff",
            backgroundColor: "#635f5fff",
            "&:hover": { backgroundColor: "#333", borderColor: "#333" },
          }}
        >
          Back to Groups
        </Button>
      </div>

      <Paper sx={{ width: "100%", p: 2 }}>
        <Box sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
          {" "}
          {/* "#f8f9fa" */}
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "var(--color-primary)", fontWeight: 600, mb: 2 }}
          >
            Group Details
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Group Name */}
            <TextField
              label="Group Name"
              value={formData.name}
              // onChange={(e) => handleChange("name", e.target.value)}
              inputProps={{ maxLength: 30 }}
              onChange={(e) =>
                handleFieldChange(
                  "name",
                  sanitizeInput(e.target.value),
                  setFormData,
                  setErrors
                )
              }
              autoFocus
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              size="medium"
              sx={{ maxWidth: "300px", borderRadius: 2, background: "#fff" }}
              required
            />

            {/* Operators and Clients Selection in same row */}
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              {/* Operators Selection */}
              <FormControl
                sx={{
                  minWidth: "300px",
                  borderRadius: 2,
                  background: "#fff",
                }}
                error={!!validationErrors.operatorIds}
                size="small"
              >
                <InputLabel>Assign Operators</InputLabel>
                <Select
                  multiple
                  value={formData.operatorIds}
                  input={<OutlinedInput label="Assign Operators " />}
                  onChange={(e) => handleChange("operatorIds", e.target.value)}
                  renderValue={(selected) => {
                    // Show chips for up to 2, then show count (smaller input field)
                    if (selected.length <= 2) {
                      return (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((value) => {
                            const operator = operators.find((op) => op.id === value);
                            const name = operator ? operator.name : "Unknown";
                            return <Chip key={value} label={name} size="small" />;
                          })}
                        </Box>
                      );
                    } else {
                      const displayedItems = selected.slice(0, 2);
                      const remainingCount = selected.length - 2;
                      return (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                          {displayedItems.map((value) => {
                            const operator = operators.find((op) => op.id === value);
                            const name = operator ? operator.name : "Unknown";
                            return <Chip key={value} label={name} size="small" />;
                          })}
                          <Chip label={`+${remainingCount} more`} size="small" color="primary" variant="outlined" />
                        </Box>
                      );
                    }
                  }}
                  MenuProps={{
                    ...MenuProps,
                    autoFocus: false,
                    disableAutoFocusItem: true,
                  }}
                  sx={{ maxWidth: "auto" }}
                >
                  <MenuItem
                    disableRipple
                    sx={{
                      "&:hover": { backgroundColor: "transparent" },
                      cursor: "default",
                      pointerEvents: "none",
                    }}
                  >
                    <TextField
                      variant="outlined"
                      size="small"
                      placeholder="Search Operators"
                      value={searchText}
                      onChange={handleOperatorSearch}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      autoFocus={false}
                      sx={{
                        mb: 1,
                        pointerEvents: "all",
                        width: "100%",
                      }}
                    />
                  </MenuItem>
                  {(searchText ? filterOperators : operators).map(
                    (operator) => (
                      <MenuItem key={operator.id} value={operator.id}>
                        <Checkbox
                          checked={
                            formData.operatorIds.indexOf(operator.id) > -1
                          }
                        />
                        <ListItemText
                          primary={`${operator.name}`}
                          secondary={operator.email}
                        />
                      </MenuItem>
                    )
                  )}
                </Select>
                {validationErrors.operatorIds && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, ml: 2 }}
                  >
                    {validationErrors.operatorIds}
                  </Typography>
                )}
              </FormControl>

              {/* Clients Selection */}
              <FormControl
                sx={{
                  maxWidth: "auto",
                  minWidth: "300px",
                  borderRadius: 2,
                  background: "#fff",
                }}
                error={!!validationErrors.clientIds}
                size="small"
              >
                <InputLabel>Assign Clients</InputLabel>
                <Select
                  multiple
                  value={formData.clientIds}
                  onChange={(e) => handleChange("clientIds", e.target.value)}
                  input={<OutlinedInput label="Assign Clients " />}
                  renderValue={(selected) => {
                    // Show chips for up to 2, then show count (smaller input field)
                    if (selected.length <= 2) {
                      return (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((value) => {
                            const client = clients.find((c) => c.id === value);
                            return (
                              <Chip key={value} label={client?.name || "Unknown"} size="small" />
                            );
                          })}
                        </Box>
                      );
                    } else {
                      const displayedItems = selected.slice(0, 2);
                      const remainingCount = selected.length - 2;
                      return (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                          {displayedItems.map((value) => {
                            const client = clients.find((c) => c.id === value);
                            return (
                              <Chip key={value} label={client?.name || "Unknown"} size="small" />
                            );
                          })}
                          <Chip label={`+${remainingCount} more`} size="small" color="primary" variant="outlined" />
                        </Box>
                      );
                    }
                  }}
                  MenuProps={{
                    ...MenuProps,
                    autoFocus: false,
                    disableAutoFocusItem: true,
                  }}
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      <Checkbox
                        checked={formData.clientIds.indexOf(client.id) > -1}
                      />
                      <ListItemText
                        primary={client.name}
                        secondary={client.email}
                      />
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.clientIds && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, ml: 2 }}
                  >
                    {validationErrors.clientIds}
                  </Typography>
                )}
              </FormControl>
            </Box>

            {/* Current Assignments Summary */}
            <Box
              sx={{ mt: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 1 }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                Assignment Summary:
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>
                  Selected Operators ({formData.operatorIds.length}):
                </strong>
                {formData.operatorIds.length > 0
                  ? " " +
                    getSelectedNames(formData.operatorIds, operators).join(", ")
                  : " None selected"}
              </Typography>
              <Typography variant="body2">
                <strong>Selected Clients ({formData.clientIds.length}):</strong>
                {formData.clientIds.length > 0
                  ? " " +
                    getSelectedNames(formData.clientIds, clients).join(", ")
                  : " None selected"}
              </Typography>
            </Box>

            {/* Save Changes Button */}
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading}
                sx={{
                  backgroundColor: "var(--color-primary)",
                  "&:hover": {
                    backgroundColor: "var(--color-secondary)",
                  },
                  minWidth: "140px",
                }}
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </Box>

            {/* Info Alert */}
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Assignment Logic:</strong> Operators assigned to this
                group will be able to view and respond to messages from all
                assigned clients. Each operator in the group has equal access to
                handle client communications.
              </Typography>
            </Alert>
          </Box>
        </Box>
      </Paper>
    </div>
  );
}
